import { ChangeEvent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityDashboardService } from '../../admin/alumni-activity-dashboard/activityDashboardService';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import SessionContext from '../../../library/Core/SessionContext';
import { getInstituteFlags } from '../../../library/Core/SmartFunctions';
import FactLoader from '../../../library/SimpleControls/FactLoader';
import MentorshipAllCampuses from './MentorshipAllCampuses';
import MentorshipCampusConfig from './MentorshipCampusConfig';
import MentorshipCohortForm, { MentorshipCohortFormValue } from './MentorshipCohortForm';
import MentorshipCohortSelector from './MentorshipCohortSelector';
import MentorshipDomainTaxonomy from './MentorshipDomainTaxonomy';
import MentorshipDefinitionOfDone from './MentorshipDefinitionOfDone';
import MentorshipSessionsPlatform from './MentorshipSessionsPlatform';
import MentorshipSessionLog from './MentorshipSessionLog';
import MentorshipMentorPool from './MentorshipMentorPool';
import MentorshipMenteePool from './MentorshipMenteePool';
import MentorshipMatching from './MentorshipMatching';
import MentorshipNotifications from './MentorshipNotifications';
import MentorshipOverview from './MentorshipOverview';
import './MentorshipOverviewVisual.css';
import MentorshipService, {
    MentorshipCampusConfig as MentorshipCampusConfigModel,
    MentorshipCampusConfigPayload,
    MentorshipCohortDetail,
    MentorshipCohortListItem,
} from './MentorshipService';

const PHASE1_ROLE = 'INSTITUTE_ADMIN';
const SUPER_ADMIN_ROLE = 'SUPER_ADMIN';
const SESSION_GROUP_KEY = 'mentorship_selected_group';
const ALL_CAMPUSES_VIEW = 'all';
const SECTION_OVERVIEW = 'overview';
const SECTION_CAMPUS = 'campus';
const SECTION_TAXONOMY = 'taxonomy';
const SECTION_DOD = 'dod';
const SECTION_SESSIONS = 'sessions-platform';
const SECTION_SESSION_LOG = 'session-log';
const SECTION_POOL = 'pool';
const SECTION_MENTEE_POOL = 'mentee-pool';
const MENTEE_POOL_DISABLED_TITLE =
    'Select a mentor experience threshold and at least one eligible student program in Campus Configuration to enable Mentee Pool';
const MENTOR_POOL_DISABLED_TITLE =
    'Add at least one career track in Domain Taxonomy to enable Mentor Pool';

/** Mentee Pool can only fetch mentees when both campus conditions are saved. */
const campusConfigCanFetchMentees = (config: MentorshipCampusConfigModel | null): boolean => {
    if (!config) return false;
    const hasThreshold =
        config.mentorExperienceThreshold != null && String(config.mentorExperienceThreshold).trim() !== '';
    const hasPrograms = Array.isArray(config.eligiblePrograms) && config.eligiblePrograms.length > 0;
    return hasThreshold && hasPrograms;
};
const SECTION_MATCHING = 'matching';
const SECTION_NOTIFICATIONS = 'notifications';
const SECTION_COHORT = 'cohort';

const emptyIdentity = (campusIds: number[] = []): MentorshipCohortFormValue => ({
    name: '',
    startDate: null,
    endDate: null,
    reshuffleEnabled: false,
    campusIds,
});

const identityFromCohort = (cohort: MentorshipCohortDetail | MentorshipCohortListItem): MentorshipCohortFormValue => ({
    name: cohort.name ?? '',
    startDate: cohort.startDate,
    endDate: cohort.endDate,
    reshuffleEnabled: MentorshipService.asBoolean(cohort.reshuffleEnabled),
    campusIds: cohort.associatedCampusIds || [],
});

const identitiesEqual = (left: MentorshipCohortFormValue, right: MentorshipCohortFormValue) => {
    const ids = (value: MentorshipCohortFormValue) =>
        [...(value.campusIds || [])].sort((a, b) => a - b).join(',');
    return (
        (left.name ?? '') === (right.name ?? '') &&
        (left.startDate || null) === (right.startDate || null) &&
        (left.endDate || null) === (right.endDate || null) &&
        MentorshipService.asBoolean(left.reshuffleEnabled) === MentorshipService.asBoolean(right.reshuffleEnabled) &&
        ids(left) === ids(right)
    );
};

const MentorshipModuleSettings = () => {
    const { sessionState } = useContext(SessionContext);
    const { globalState } = useContext(GlobalContext);

    const isInstituteAdmin = sessionState?.userTypeCode === PHASE1_ROLE;
    const isSuperAdmin = sessionState?.userTypeCode === SUPER_ADMIN_ROLE;
    const isMentorshipAdmin = isInstituteAdmin || isSuperAdmin;
    const contextInstituteId = sessionState?.primaryInstitute || sessionState?.instituteId;
    const flagState = { domain: globalState?.domain } as any;
    const mentorshipHeaderFromSnapshot = Boolean(
        getInstituteFlags(flagState, contextInstituteId ? [String(contextInstituteId)] : []).mentorshipHeader
    );
    const mentorshipHeader = mentorshipHeaderFromSnapshot;

    const [selectedInstituteId, setSelectedInstituteId] = useState('');
    const [groups, setGroups] = useState<any[]>([]);
    const [selectedGroupValue, setSelectedGroupValue] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState<string | number | null>(null);
    const [instSearch, setInstSearch] = useState('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [instLoading, setInstLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [instSelected, setInstSelected] = useState(false);
    const debounceRef = useRef<any>(null);
    const instituteWrapperRef = useRef<HTMLDivElement>(null);
    const instituteQueryId = isSuperAdmin ? MentorshipService.toNumberOrNull(selectedInstituteId) : undefined;

    const campuses = useMemo(() => {
        if (isSuperAdmin) {
            return MentorshipService.getGroupCampusesFromDomain(globalState?.domain, selectedInstituteId);
        }
        return MentorshipService.getAdminCampuses(globalState?.domain, sessionState?.institutes);
    }, [globalState?.domain, sessionState?.institutes, isSuperAdmin, selectedInstituteId]);
    const isGroup = campuses.length > 1;

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [cohorts, setCohorts] = useState<MentorshipCohortListItem[]>([]);
    const [selectedCohortId, setSelectedCohortId] = useState<number | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [cohortDetail, setCohortDetail] = useState<MentorshipCohortDetail | null>(null);
    const [identity, setIdentity] = useState<MentorshipCohortFormValue>(emptyIdentity());
    const [savedIdentity, setSavedIdentity] = useState<MentorshipCohortFormValue>(emptyIdentity());
    const [activeView, setActiveView] = useState<string>(ALL_CAMPUSES_VIEW);
    const [innerSection, setInnerSection] = useState<string>(SECTION_OVERVIEW);
    const [tabBadges, setTabBadges] = useState<{
        mentorPool: number | null;
        menteePool: number | null;
        matching: number | null;
    }>({ mentorPool: null, menteePool: null, matching: null });
    /**
     * Career tracks come from Domain Taxonomy. Mentor Pool's Manual Add renders a Career Tracks
     * picker from them, so with zero tracks that field is an empty label. null = not known yet
     * (or the fetch failed) — the tab stays usable rather than being blocked on a failed read.
     */
    const [taxonomyTrackCount, setTaxonomyTrackCount] = useState<number | null>(null);
    /** Bump after pool membership add/remove so pill badges re-fetch totals. */
    const [tabBadgeEpoch, setTabBadgeEpoch] = useState(0);
    const requestTabBadgeReload = () => setTabBadgeEpoch((current) => current + 1);
    const [mentorshipAccessBlocked, setMentorshipAccessBlocked] = useState(false);

    const settingsEnabled = isCreating || selectedCohortId != null;
    const campusConfigEnabled = !isCreating && selectedCohortId != null;
    const showModuleTabs = campusConfigEnabled;
    const identityDirty = isCreating || !identitiesEqual(identity, savedIdentity);
    const createIdentityComplete = MentorshipService.isCreateCohortIdentityComplete(identity);
    const canSaveIdentity = settingsEnabled && identityDirty && (!isCreating || createIdentityComplete);

    const associatedCampusIds = cohortDetail?.associatedCampusIds?.length
        ? cohortDetail.associatedCampusIds
        : identity.campusIds;
    const associatedCampuses = campuses.filter((campus) => associatedCampusIds.includes(campus.value));
    const showAllCampuses = isGroup && associatedCampusIds.length >= 2 && campusConfigEnabled;

    const loadCohorts = async (preferredId?: number | null, ignoreCurrentSelection = false) => {
        setIsLoading(true);
        setErrorMessage('');
        try {
            const list = await MentorshipService.listCohorts(instituteQueryId);
            setCohorts(list);
            const nextId = ignoreCurrentSelection ? preferredId ?? null : preferredId ?? selectedCohortId;
            if (nextId && list.some((item) => item.id === nextId)) {
                setSelectedCohortId(nextId);
            } else if (!isCreating || ignoreCurrentSelection) {
                setSelectedCohortId(null);
                setCohortDetail(null);
                const cleared = emptyIdentity(isGroup ? [] : campuses.map((campus) => campus.value));
                setIdentity(cleared);
                setSavedIdentity(cleared);
            }
        } catch (error) {
            if (isInstituteAdmin && MentorshipService.isMentorshipFeatureGateError(error)) {
                setMentorshipAccessBlocked(true);
                setErrorMessage('');
                return;
            }
            setErrorMessage(MentorshipService.getErrorMessage(error, { instituteAdmin: isInstituteAdmin }));
        } finally {
            setIsLoading(false);
        }
    };

    const loadCohortDetail = async (cohortId: number) => {
        setIsLoading(true);
        setErrorMessage('');
        try {
            const detail = await MentorshipService.getCohort(cohortId, instituteQueryId);
            setCohortDetail(detail);
            const nextIdentity = identityFromCohort(detail);
            setIdentity(nextIdentity);
            setSavedIdentity(nextIdentity);
            const firstCampusId = detail.associatedCampusIds[0] ?? detail.campuses[0]?.campusId ?? null;
            setActiveView(detail.associatedCampusIds.length >= 2 && isGroup ? ALL_CAMPUSES_VIEW : String(firstCampusId ?? ''));
        } catch (error) {
            if (isInstituteAdmin && MentorshipService.isMentorshipFeatureGateError(error)) {
                setMentorshipAccessBlocked(true);
                setErrorMessage('');
                return;
            }
            setErrorMessage(MentorshipService.getErrorMessage(error, { instituteAdmin: isInstituteAdmin }));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        MentorshipService.setRequestInstituteId(isSuperAdmin ? instituteQueryId ?? null : null);
        return () => {
            MentorshipService.setRequestInstituteId(null);
        };
    }, [isSuperAdmin, instituteQueryId]);

    useEffect(() => {
        if (!isSuperAdmin) return;
        ActivityDashboardService.getGroups()
            .then((r) => {
                const list = r.data?.data || [];
                setGroups(list);
                const saved = sessionStorage.getItem(SESSION_GROUP_KEY);
                if (!saved) return;
                try {
                    const { id } = JSON.parse(saved);
                    const match = list.find((g: any) => String(g.id) === String(id));
                    if (match) {
                        setSelectedGroupValue(String(match.id));
                        setSelectedGroupId(match.id);
                    }
                } catch {
                    /* ignore invalid session value */
                }
            })
            .catch(() => {});
    }, [isSuperAdmin]);

    useEffect(() => {
        if (!isSuperAdmin || instSelected) return;
        clearTimeout(debounceRef.current);
        if (instSearch.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setInstLoading(true);
            try {
                const r = await ActivityDashboardService.searchInstitutes(instSearch, selectedGroupId ?? undefined);
                setSuggestions(r.data?.data || []);
                setShowSuggestions(true);
            } catch {
                setSuggestions([]);
                setShowSuggestions(true);
            } finally {
                setInstLoading(false);
            }
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [isSuperAdmin, instSearch, selectedGroupId, instSelected]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (instituteWrapperRef.current && !instituteWrapperRef.current.contains(e.target as Node)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        if (isSuperAdmin) {
            setCohorts([]);
            setSelectedCohortId(null);
            setIsCreating(false);
            setCohortDetail(null);
            setIdentity(emptyIdentity());
            setSavedIdentity(emptyIdentity());
            setActiveView(ALL_CAMPUSES_VIEW);
            setInnerSection(SECTION_OVERVIEW);
            setErrorMessage('');
            if (!selectedInstituteId) return;
            loadCohorts(null, true);
            return;
        }
        if (!isInstituteAdmin || !mentorshipHeader) return;
        setMentorshipAccessBlocked(false);
        loadCohorts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInstituteAdmin, isSuperAdmin, mentorshipHeader, selectedInstituteId]);

    useEffect(() => {
        if (isSuperAdmin && !selectedInstituteId) return;
        if (isInstituteAdmin && !mentorshipHeader) return;
        if (!isInstituteAdmin && !isSuperAdmin) return;
        if (isCreating || selectedCohortId == null) return;
        loadCohortDetail(selectedCohortId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCohortId, isCreating, isInstituteAdmin, isSuperAdmin, mentorshipHeader, selectedInstituteId]);

    useEffect(() => {
        // Badge sources (never hardcoded): Mentor Pool = listMentorPool pagination.total;
        // Mentee Pool = listMenteePool pagination.total; Matching = getMatchingStats.distinctMenteesPaired.
        if (!campusConfigEnabled || selectedCohortId == null) {
            setTabBadges({ mentorPool: null, menteePool: null, matching: null });
            setTaxonomyTrackCount(null);
            return;
        }
        let cancelled = false;
        const loadBadges = async () => {
            const [mentorResult, menteeResult, statsResult, taxonomyResult] = await Promise.allSettled([
                MentorshipService.listMentorPool(selectedCohortId, { page: 1, pageSize: 1 }, instituteQueryId),
                MentorshipService.listMenteePool(selectedCohortId, { page: 1, pageSize: 1 }, instituteQueryId),
                MentorshipService.getMatchingStats(selectedCohortId, instituteQueryId),
                MentorshipService.getDomainTaxonomy(selectedCohortId, instituteQueryId),
            ]);
            if (cancelled) return;
            setTaxonomyTrackCount(
                taxonomyResult.status === 'fulfilled' ? (taxonomyResult.value.tracks || []).length : null
            );
            setTabBadges({
                mentorPool:
                    mentorResult.status === 'fulfilled' ? mentorResult.value.pagination.total : null,
                menteePool:
                    menteeResult.status === 'fulfilled' ? menteeResult.value.pagination.total : null,
                matching:
                    statsResult.status === 'fulfilled'
                        ? statsResult.value.distinctMenteesPaired
                        : null,
            });
        };
        loadBadges();
        return () => {
            cancelled = true;
        };
    }, [campusConfigEnabled, selectedCohortId, instituteQueryId, tabBadgeEpoch]);

    // Mentee Pool fetches mentees using the campus mentor-experience threshold and the eligible
    // student programs. With neither saved there is no condition to apply, so the tab is gated.
    // Cohort-wide (not per selected campus) because MentorshipMenteePool loads every associated
    // campus at once; one configured campus is enough to make the tab usable.
    const menteePoolReady = useMemo(
        () => (cohortDetail?.campuses || []).some(campusConfigCanFetchMentees),
        [cohortDetail]
    );

    // Mentor Pool's Manual Add builds its Career Tracks picker from Domain Taxonomy. With zero
    // tracks that field renders as a bare label, so the tab is gated until taxonomy exists.
    // Unknown count (not loaded, or the read failed) leaves the tab usable.
    const mentorPoolReady = taxonomyTrackCount == null || taxonomyTrackCount > 0;

    // Switching cohorts (or an Overview quick action) can land on a gated section while the new
    // cohort is unconfigured. Leave it instead of stranding the admin on a disabled tab.
    useEffect(() => {
        if (!cohortDetail) return;
        if (!menteePoolReady && innerSection === SECTION_MENTEE_POOL) {
            setInnerSection(SECTION_OVERVIEW);
        } else if (!mentorPoolReady && innerSection === SECTION_POOL) {
            setInnerSection(SECTION_OVERVIEW);
        }
    }, [cohortDetail, menteePoolReady, mentorPoolReady, innerSection]);

    const handleSelectCohort = (cohortId: number | null) => {
        setIsCreating(false);
        setSelectedCohortId(cohortId);
        if (cohortId == null) {
            setCohortDetail(null);
            const cleared = emptyIdentity(isGroup ? [] : campuses.map((campus) => campus.value));
            setIdentity(cleared);
            setSavedIdentity(cleared);
        }
    };

    const handleAddNew = () => {
        setIsCreating(true);
        setSelectedCohortId(null);
        setCohortDetail(null);
        setIdentity(emptyIdentity(isGroup ? [] : campuses.map((campus) => campus.value)));
        setSavedIdentity(emptyIdentity(isGroup ? [] : campuses.map((campus) => campus.value)));
        setErrorMessage('');
        setInnerSection(SECTION_COHORT);
    };

    const handleDiscard = () => {
        if (isCreating) {
            setIsCreating(false);
            setIdentity(emptyIdentity(isGroup ? [] : campuses.map((campus) => campus.value)));
            setSavedIdentity(emptyIdentity(isGroup ? [] : campuses.map((campus) => campus.value)));
            setErrorMessage('');
            return;
        }
        setIdentity(savedIdentity);
        setErrorMessage('');
    };

    const applyCohortIdentityFields = (detail: MentorshipCohortDetail) => {
        const nextIdentity = identityFromCohort(detail);
        setIdentity(nextIdentity);
        setSavedIdentity(nextIdentity);
        setCohorts((current) => {
            const without = current.filter((item) => item.id !== detail.id);
            return [MentorshipService.mapCohortIdentity(detail), ...without];
        });
    };

    const applyDetail = (detail: MentorshipCohortDetail) => {
        setIsCreating(false);
        setSelectedCohortId(detail.id);
        setCohortDetail(detail);
        applyCohortIdentityFields(detail);
        const firstCampusId = detail.associatedCampusIds[0] ?? detail.campuses[0]?.campusId ?? null;
        setActiveView(detail.associatedCampusIds.length >= 2 && isGroup ? ALL_CAMPUSES_VIEW : String(firstCampusId ?? ''));
    };

    const applyIdentitySave = (detail: MentorshipCohortDetail) => {
        applyCohortIdentityFields(detail);
        setCohortDetail((current) => {
            if (!current || current.id !== detail.id) {
                return detail;
            }
            return {
                ...current,
                ...MentorshipService.mapCohortIdentity(detail),
                campuses: current.campuses,
            };
        });
    };

    const handleSaveIdentity = async () => {
        if (!settingsEnabled || isSaving || !identityDirty) return;
        if (isCreating) {
            const name = identity.name == null ? '' : String(identity.name).trim();
            if (!name) {
                setErrorMessage('name is required');
                return;
            }
            if (!String(identity.startDate ?? '').trim()) {
                setErrorMessage('startDate is required');
                return;
            }
            if (!String(identity.endDate ?? '').trim()) {
                setErrorMessage('endDate is required');
                return;
            }
            const today = MentorshipService.todayCalendarDate();
            if (identity.startDate && identity.startDate < today) {
                setErrorMessage('startDate must not be in the past');
                return;
            }
            if (identity.endDate) {
                if (identity.startDate && identity.endDate < identity.startDate) {
                    setErrorMessage('endDate must not be before startDate');
                    return;
                }
                if (!identity.startDate && identity.endDate < today) {
                    setErrorMessage('endDate must not be in the past');
                    return;
                }
            }
        }
        setIsSaving(true);
        setErrorMessage('');
        try {
            if (isCreating) {
                const payload = {
                    name: String(identity.name ?? '').trim(),
                    startDate: identity.startDate,
                    endDate: identity.endDate,
                    reshuffleEnabled: identity.reshuffleEnabled,
                    ...(isGroup ? { campusIds: identity.campusIds } : {}),
                };
                const created = await MentorshipService.createCohort(payload, instituteQueryId);
                applyDetail(created);
            } else if (selectedCohortId != null) {
                const updated = await MentorshipService.updateCohort(selectedCohortId, {
                    name: identity.name,
                    startDate: identity.startDate,
                    endDate: identity.endDate,
                    reshuffleEnabled: identity.reshuffleEnabled,
                }, instituteQueryId);
                applyIdentitySave(updated);
            }
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveCampus = async (campusId: number, payload: MentorshipCampusConfigPayload) => {
        if (selectedCohortId == null) return;
        setIsSaving(true);
        setErrorMessage('');
        try {
            const saved = await MentorshipService.saveCampusConfig(selectedCohortId, campusId, payload, instituteQueryId);
            setCohortDetail((current) => {
                if (!current) return current;
                const campusesNext = current.campuses.some((item) => Number(item.campusId) === Number(campusId))
                    ? current.campuses.map((item) =>
                          Number(item.campusId) === Number(campusId) ? { ...item, ...saved } : item
                      )
                    : [...current.campuses, saved];
                return { ...current, campuses: campusesNext };
            });
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveAllCampuses = async (payload: MentorshipCampusConfigPayload) => {
        if (selectedCohortId == null) return;
        setIsSaving(true);
        setErrorMessage('');
        try {
            const updated = await MentorshipService.saveAllCampusesConfig(selectedCohortId, payload, instituteQueryId);
            applyDetail(updated);
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveMergedCampusConfigs = async (
        updates: { campusId: number; payload: MentorshipCampusConfigPayload }[]
    ) => {
        if (selectedCohortId == null || updates.length === 0) return;
        setIsSaving(true);
        setErrorMessage('');
        try {
            const savedRows: MentorshipCampusConfigModel[] = [];
            for (const item of updates) {
                savedRows.push(
                    await MentorshipService.saveCampusConfig(selectedCohortId, item.campusId, item.payload, instituteQueryId)
                );
            }
            setCohortDetail((current) => {
                if (!current) return current;
                let campusesNext = [...current.campuses];
                savedRows.forEach((saved) => {
                    const index = campusesNext.findIndex((row) => Number(row.campusId) === Number(saved.campusId));
                    if (index >= 0) {
                        campusesNext[index] = { ...campusesNext[index], ...saved };
                    } else {
                        campusesNext = [...campusesNext, saved];
                    }
                });
                return { ...current, campuses: campusesNext };
            });
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const handleGroupChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setSelectedGroupValue(val);
        const opt = groups.find((g: any) => String(g.id) === val);
        const id = opt ? opt.id : null;
        const name = opt ? opt.name : '';
        if (id) sessionStorage.setItem(SESSION_GROUP_KEY, JSON.stringify({ id, name }));
        else sessionStorage.removeItem(SESSION_GROUP_KEY);
        setSelectedGroupId(id);
        setInstSearch('');
        setInstSelected(false);
        setSuggestions([]);
        setShowSuggestions(false);
        setSelectedInstituteId('');
    };

    const handleSelectInstitute = (inst: any) => {
        setInstSearch(inst.name);
        setInstSelected(true);
        setShowSuggestions(false);
        setSuggestions([]);
        setSelectedInstituteId(String(inst.id));
    };

    const handleClearInstitute = () => {
        setInstSearch('');
        setInstSelected(false);
        setSuggestions([]);
        setShowSuggestions(false);
        setSelectedInstituteId('');
    };

    const campusConfigFor = (campusId: number): MentorshipCampusConfigModel | null =>
        cohortDetail?.campuses.find((item) => Number(item.campusId) === Number(campusId)) || null;

    // Programs come from the campus config API (same database as the eligible-student count).
    // The ALL_PROGRAM_CODE domain snapshot is only a fallback: it is published from a different
    // environment and has gone empty for whole institutes, which showed as "No programs".
    const apiProgramRows = useMemo(
        () => (cohortDetail?.campuses || []).flatMap((campus: any) => campus.programs || []),
        [cohortDetail]
    );

    const programsForCampus = (campusId: number) => {
        const fromApi = MentorshipService.programsFromRows(apiProgramRows, [campusId]);
        return fromApi.length > 0 ? fromApi : MentorshipService.programsForCampuses(globalState?.domain, [campusId]);
    };
    const apiProgramsForAllCampuses = MentorshipService.programsFromRows(apiProgramRows, associatedCampusIds);
    const programsForAllCampuses =
        apiProgramsForAllCampuses.length > 0
            ? apiProgramsForAllCampuses
            : MentorshipService.programsForCampuses(globalState?.domain, associatedCampusIds);

    if (!isMentorshipAdmin) {
        return (
            <div className="main flex-1">
                <div className="p-4">Mentorship Module Settings is available to Institute Admin and Super Admin only.</div>
            </div>
        );
    }

    const renderMentorshipSubscriptionDisabled = () => (
        <div className="main flex-1">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12 mb-4">
                        <div className="p-4 p-md-5">
                            <h2 className="font-26 font-600 mb-3">Module Settings</h2>
                            <div className="alert alert-info mb-0">
                                {MentorshipService.MENTORSHIP_SUBSCRIPTION_DISABLED_MESSAGE}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    if (isInstituteAdmin && (!mentorshipHeader || mentorshipAccessBlocked)) {
        return renderMentorshipSubscriptionDisabled();
    }

    return (
        <div className="main flex-1">
            <FactLoader show={isLoading} />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12 mb-4">
                        <div className="">
                            <div className="p-4 p-md-5">
                                <h2 className="font-26 font-600 mb-3">Module Settings</h2>

                                {isSuperAdmin && (
                                    <div className="bg-white border rounded shadow-sm p-3 mb-4">
                                        <div className="row g-3 align-items-end">
                                            <div className="col-md-4">
                                                <label className="form-label small fw-semibold text-uppercase text-muted mb-1">
                                                    <i className="bi bi-buildings me-1"></i>Group
                                                </label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={selectedGroupValue}
                                                    onChange={handleGroupChange}
                                                >
                                                    <option value="">All Groups</option>
                                                    {groups.map((g: any) => (
                                                        <option key={g.id} value={g.id}>{g.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-6" ref={instituteWrapperRef} style={{ position: 'relative' }}>
                                                <label className="form-label small fw-semibold text-uppercase text-muted mb-1">
                                                    <i className="bi bi-mortarboard me-1"></i>Institute
                                                </label>
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text bg-white">
                                                        {instLoading
                                                            ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                                                            : <i className="bi bi-search text-muted"></i>}
                                                    </span>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search institute… (min 2 chars)"
                                                        value={instSearch}
                                                        autoComplete="off"
                                                        onChange={(e) => {
                                                            setInstSearch(e.target.value);
                                                            setInstSelected(false);
                                                            if (!e.target.value) setSelectedInstituteId('');
                                                        }}
                                                        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                                                    />
                                                    {instSearch && (
                                                        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={handleClearInstitute}>
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                                {showSuggestions && (
                                                    <div
                                                        className="border rounded bg-white shadow position-absolute w-100 mt-1"
                                                        style={{ zIndex: 1050, maxHeight: 300, overflowY: 'auto', top: '100%' }}
                                                    >
                                                        {suggestions.length > 0 ? suggestions.map((inst: any) => (
                                                            <div
                                                                key={inst.id}
                                                                className="px-3 py-2 border-bottom"
                                                                style={{ cursor: 'pointer' }}
                                                                onMouseDown={(e) => { e.preventDefault(); handleSelectInstitute(inst); }}
                                                            >
                                                                <div className="fw-semibold font-13">{inst.name}</div>
                                                                {inst.group_name && (
                                                                    <div className="text-muted" style={{ fontSize: 11 }}>{inst.group_name}</div>
                                                                )}
                                                                {inst.city_name && (
                                                                    <div className="text-muted" style={{ fontSize: 11 }}>
                                                                        <i className="bi bi-geo-alt me-1"></i>{inst.city_name}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )) : (
                                                            <div className="text-muted small px-3 py-2">
                                                                No institutes found.{selectedGroupId ? ' Try a different name or clear the group filter.' : ' Try a different name.'}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

                                {isSuperAdmin && !selectedInstituteId ? (
                                    <div className="text-muted">Select an institute to manage Mentorship.</div>
                                ) : (
                                <>

                                <MentorshipCohortSelector
                                    cohorts={cohorts}
                                    selectedCohortId={selectedCohortId}
                                    isCreating={isCreating}
                                    onSelect={handleSelectCohort}
                                    onAddNew={handleAddNew}
                                />

                                {showModuleTabs && (
                                <ul className="ms-pill-nav flex-nowrap overflow-auto">
                                    <MentorshipPillTab
                                        active={innerSection === SECTION_OVERVIEW}
                                        icon="bi-grid-1x2-fill"
                                        label="Overview"
                                        onClick={() => setInnerSection(SECTION_OVERVIEW)}
                                    />
                                    <MentorshipPillTab
                                        active={innerSection === SECTION_CAMPUS}
                                        icon="bi-building"
                                        label="Campus Configuration"
                                        onClick={() => setInnerSection(SECTION_CAMPUS)}
                                    />
                                    <MentorshipPillTab
                                        active={innerSection === SECTION_TAXONOMY}
                                        icon="bi-diagram-3-fill"
                                        label="Domain Taxonomy"
                                        onClick={() => setInnerSection(SECTION_TAXONOMY)}
                                    />
                                    <MentorshipPillTab
                                        active={innerSection === SECTION_DOD}
                                        icon="bi-check2-square"
                                        label="Definition of Done"
                                        onClick={() => setInnerSection(SECTION_DOD)}
                                    />
                                    <MentorshipPillTab
                                        active={innerSection === SECTION_SESSIONS}
                                        icon="bi-camera-video-fill"
                                        label="Sessions & Platform"
                                        onClick={() => setInnerSection(SECTION_SESSIONS)}
                                    />
                                    <MentorshipPillTab
                                        active={innerSection === SECTION_SESSION_LOG}
                                        icon="bi-journal-text"
                                        label="Session Log"
                                        onClick={() => setInnerSection(SECTION_SESSION_LOG)}
                                    />
                                    <MentorshipPillTab
                                        active={innerSection === SECTION_POOL}
                                        icon="bi-people-fill"
                                        label="Mentor Pool"
                                        badge={tabBadges.mentorPool}
                                        badgeTone="pool"
                                        disabled={!mentorPoolReady}
                                        disabledTitle={MENTOR_POOL_DISABLED_TITLE}
                                        onClick={() => setInnerSection(SECTION_POOL)}
                                    />
                                    <MentorshipPillTab
                                        active={innerSection === SECTION_MENTEE_POOL}
                                        icon="bi-mortarboard-fill"
                                        label="Mentee Pool"
                                        badge={tabBadges.menteePool}
                                        badgeTone="pool"
                                        disabled={!menteePoolReady}
                                        disabledTitle={MENTEE_POOL_DISABLED_TITLE}
                                        onClick={() => setInnerSection(SECTION_MENTEE_POOL)}
                                    />
                                    <MentorshipPillTab
                                        active={innerSection === SECTION_MATCHING}
                                        icon="bi-link-45deg"
                                        label="Matching"
                                        badge={tabBadges.matching}
                                        badgeTone="match"
                                        onClick={() => setInnerSection(SECTION_MATCHING)}
                                    />
                                    <MentorshipPillTab
                                        active={innerSection === SECTION_NOTIFICATIONS}
                                        icon="bi-bell-fill"
                                        label="Notifications"
                                        onClick={() => setInnerSection(SECTION_NOTIFICATIONS)}
                                    />
                                    <MentorshipPillTab
                                        active={innerSection === SECTION_COHORT}
                                        icon="bi-gear-fill"
                                        label="Cohort Settings"
                                        onClick={() => setInnerSection(SECTION_COHORT)}
                                    />
                                </ul>
                                )}

                                {showModuleTabs && innerSection === SECTION_OVERVIEW && (
                                    <MentorshipOverview
                                        key={`${selectedInstituteId || 'ia'}-${selectedCohortId ?? 'none'}-overview`}
                                        cohortId={campusConfigEnabled ? selectedCohortId : null}
                                        disabled={!campusConfigEnabled}
                                        cohortName={cohortDetail?.name ?? null}
                                        instituteId={instituteQueryId ?? null}
                                        onConfigureModule={() => setInnerSection(SECTION_CAMPUS)}
                                        onNavigateSection={(section) => setInnerSection(section)}
                                    />
                                )}

                                {showModuleTabs && innerSection === SECTION_CAMPUS && (
                                    <>
                                        {campusConfigEnabled && associatedCampuses.length > 0 ? (
                                            <>
                                                {isGroup && (
                                                    <div className="d-flex bg-white border border-light-subtle rounded-3 p-2 gap-1 mb-4 flex-wrap">
                                                        {showAllCampuses && (
                                                            
                                                                <button
                                                                    type="button"
                                                                    className={`btn btn-transparent font-14 font-600 border-0 rounded-2 stab   ${activeView === ALL_CAMPUSES_VIEW ? 'btn-primary text-white shadow-sm' : 'text-secondary'}`}
                                                                    onClick={() => setActiveView(ALL_CAMPUSES_VIEW)}>
                                                                    All Campuses
                                                                </button>
                                                            
                                                        )}
                                                        {associatedCampuses.map((campus) => (
                                                            <button
                                                                key={campus.value}
                                                                type="button"
                                                                className={`btn btn-transparent font-14 font-600 border-0 rounded-2 stab ${activeView === String(campus.value) ? 'btn-primary text-white shadow-sm' : 'text-secondary'}`}
                                                                onClick={() => setActiveView(String(campus.value))}>
                                                                {campus.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}

                                                {showAllCampuses && activeView === ALL_CAMPUSES_VIEW && (
                                                    <MentorshipAllCampuses
                                                        programs={programsForAllCampuses}
                                                        associatedCampusIds={associatedCampusIds}
                                                        configs={cohortDetail?.campuses || []}
                                                        disabled={!campusConfigEnabled}
                                                        saving={isSaving}
                                                        onSave={handleSaveAllCampuses}
                                                        onSaveMerged={handleSaveMergedCampusConfigs}
                                                    />
                                                )}

                                                {activeView !== ALL_CAMPUSES_VIEW && (
                                                    <div>
                                                        {(isGroup
                                                            ? associatedCampuses.filter(
                                                                  (campus) => String(campus.value) === activeView
                                                              )
                                                            : associatedCampuses
                                                        ).map((campus) => (
                                                            <MentorshipCampusConfig
                                                                key={campus.value}
                                                                idPrefix={`mentorship-campus-${campus.value}`}
                                                                config={campusConfigFor(campus.value)}
                                                                programs={programsForCampus(campus.value)}
                                                                cohortId={campusConfigEnabled ? selectedCohortId : null}
                                                                campusId={campus.value}
                                                                instituteId={instituteQueryId}
                                                                disabled={!campusConfigEnabled}
                                                                saving={isSaving}
                                                                onSave={(payload) => handleSaveCampus(campus.value, payload)}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="text-muted">Select a cohort to manage Campus Configuration.</div>
                                        )}
                                    </>
                                )}

                                {showModuleTabs && innerSection === SECTION_TAXONOMY && (
                                    <MentorshipDomainTaxonomy
                                        key={`${selectedInstituteId || 'ia'}-${selectedCohortId ?? 'none'}`}
                                        cohortId={campusConfigEnabled ? selectedCohortId : null}
                                        disabled={!campusConfigEnabled}
                                        onTracksSaved={requestTabBadgeReload}
                                    />
                                )}

                                {showModuleTabs && innerSection === SECTION_DOD && (
                                    <MentorshipDefinitionOfDone
                                        key={`${selectedInstituteId || 'ia'}-${selectedCohortId ?? 'none'}-dod`}
                                        cohortId={campusConfigEnabled ? selectedCohortId : null}
                                        disabled={!campusConfigEnabled}
                                    />
                                )}

                                {showModuleTabs && innerSection === SECTION_SESSIONS && (
                                    <MentorshipSessionsPlatform
                                        key={`${selectedInstituteId || 'ia'}-${selectedCohortId ?? 'none'}-sessions`}
                                        cohortId={campusConfigEnabled ? selectedCohortId : null}
                                        disabled={!campusConfigEnabled}
                                    />
                                )}

                                {showModuleTabs && innerSection === SECTION_SESSION_LOG && (
                                    <MentorshipSessionLog
                                        key={`${selectedInstituteId || 'ia'}-${selectedCohortId ?? 'none'}-session-log`}
                                        cohortId={campusConfigEnabled ? selectedCohortId : null}
                                        disabled={!campusConfigEnabled}
                                        campuses={associatedCampuses}
                                        instituteId={instituteQueryId ?? null}
                                    />
                                )}

                                {showModuleTabs && innerSection === SECTION_POOL && (
                                    <MentorshipMentorPool
                                        key={`${selectedInstituteId || 'ia'}-${selectedCohortId ?? 'none'}`}
                                        cohortId={campusConfigEnabled ? selectedCohortId : null}
                                        disabled={!campusConfigEnabled}
                                        campuses={associatedCampuses}
                                        isGroup={isGroup}
                                        onPoolMembershipChange={requestTabBadgeReload}
                                    />
                                )}

                                {showModuleTabs && innerSection === SECTION_MENTEE_POOL && (
                                    <MentorshipMenteePool
                                        key={`${selectedInstituteId || 'ia'}-${selectedCohortId ?? 'none'}-mentee`}
                                        cohortId={campusConfigEnabled ? selectedCohortId : null}
                                        disabled={!campusConfigEnabled}
                                        campuses={associatedCampuses}
                                        isGroup={isGroup}
                                        programs={programsForAllCampuses}
                                        eligiblePrograms={(cohortDetail?.campuses || []).flatMap(
                                            (campus) => campus.eligiblePrograms || []
                                        )}
                                        campusConfigs={cohortDetail?.campuses || []}
                                        cohortEndDate={cohortDetail?.endDate ?? null}
                                        onPoolMembershipChange={requestTabBadgeReload}
                                        onOpenEligibilitySettings={() => setInnerSection(SECTION_CAMPUS)}
                                    />
                                )}

                                {showModuleTabs && innerSection === SECTION_MATCHING && (
                                    <MentorshipMatching
                                        key={`${selectedInstituteId || 'ia'}-${selectedCohortId ?? 'none'}-matching`}
                                        cohortId={campusConfigEnabled ? selectedCohortId : null}
                                        disabled={!campusConfigEnabled}
                                    />
                                )}

                                {showModuleTabs && innerSection === SECTION_NOTIFICATIONS && (
                                    <MentorshipNotifications
                                        key={`${selectedInstituteId || 'ia'}-${selectedCohortId ?? 'none'}-notifications`}
                                        cohortId={campusConfigEnabled ? selectedCohortId : null}
                                        disabled={!campusConfigEnabled}
                                    />
                                )}

                                {(isCreating || (showModuleTabs && innerSection === SECTION_COHORT)) && (
                                    <>
                                        <div className="d-flex flex-wrap justify-content-end gap-2 mb-4">
                                            <button
                                                type="button"
                                                className="btn-green text-white btn-sm font-13 font-700"
                                                disabled={!canSaveIdentity || isSaving}
                                                onClick={handleSaveIdentity}>
                                                <span>Save Settings</span>
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm font-13 font-600 py-2 px-3 rounded-3 rounded-pill"
                                                disabled={isSaving || (!isCreating && !identityDirty)}
                                                onClick={handleDiscard}>
                                                Discard Settings
                                            </button>
                                        </div>

                                        <fieldset disabled={!settingsEnabled}>
                                            <MentorshipCohortForm
                                                value={identity}
                                                campuses={campuses}
                                                isGroup={isGroup}
                                                isCreate={isCreating}
                                                disabled={!settingsEnabled}
                                                onChange={setIdentity}
                                            />
                                        </fieldset>
                                    </>
                                )}
                                </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

function MentorshipPillTab({
    active,
    icon,
    label,
    onClick,
    badge,
    badgeTone,
    disabled = false,
    disabledTitle,
}: {
    active: boolean;
    icon: string;
    label: string;
    onClick: () => void;
    badge?: number | null;
    badgeTone?: 'pool' | 'match';
    disabled?: boolean;
    disabledTitle?: string;
}) {
    // Title sits on the wrapper: browsers suppress hover events on a disabled button, so a title
    // there would never surface the reason the tab is unavailable.
    return (
        <li title={disabled ? disabledTitle : undefined}>
            <button
                type="button"
                className={`ms-pill-tab${active ? ' is-active' : ''}${disabled ? ' text-muted opacity-50' : ''}`}
                disabled={disabled}
                aria-disabled={disabled || undefined}
                onClick={disabled ? undefined : onClick}>
                <i className={`bi ${icon}`} aria-hidden="true" />
                <span>{label}</span>
                {badge != null ? (
                    <span className={`ms-pill-badge ms-pill-badge--${badgeTone ?? 'pool'}`}>{badge}</span>
                ) : null}
            </button>
        </li>
    );
}

export default MentorshipModuleSettings;
